"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { Ruler } from "lucide-react";
import { formatCm } from "@/lib/text-metrics";
import type { FontOption } from "@/lib/fonts";
import type { ColorOption } from "@/lib/colors";
import { cn } from "@/lib/utils";

interface LivePreviewProps {
  text: string;
  font: FontOption;
  color: ColorOption;
  widthCm: number;
  heightCm: number;
}

// Base font-size the text is rendered at (single line, no wrap) before
// being scaled down to fit the panel. A real Schriftzug is one continuous
// line, so the preview must never wrap - it only ever shrinks to fit.
const BASE_PX = 88;

export function LivePreview({
  text,
  font,
  color,
  widthCm,
  heightCm,
}: LivePreviewProps) {
  const isEmpty = text.trim().length === 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const recompute = () => {
      const available = container.clientWidth - 48; // horizontal padding
      const natural = textEl.scrollWidth;
      if (natural <= 0 || available <= 0) return;
      setScale(Math.min(1, available / natural));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [text, font.family, font.weight, isEmpty]);

  return (
    <div className="flex flex-col gap-4">
      <div
        ref={containerRef}
        className="relative flex min-h-64 items-center justify-center overflow-hidden rounded-xl border bg-gradient-to-b from-muted/40 via-background to-muted/60 px-6 py-14 sm:min-h-80"
        aria-live="polite"
      >
        <span
          ref={textRef}
          className={cn(
            "inline-block whitespace-nowrap leading-none",
            isEmpty && "text-muted-foreground/40"
          )}
          style={{
            fontFamily: font.family,
            fontWeight: font.weight,
            color: isEmpty ? undefined : color.hex,
            fontSize: BASE_PX,
            transform: `scale(${scale})`,
            textShadow: isEmpty
              ? undefined
              : "0 1px 0 rgba(0,0,0,0.05), 0 10px 18px rgba(0,0,0,0.12)",
          }}
        >
          {isEmpty ? "Dein Text" : text}
        </span>

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border bg-background/90 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur">
          <Ruler className="size-3.5" />
          {formatCm(widthCm)} × {formatCm(heightCm)}
        </div>
      </div>
    </div>
  );
}
